import { useMutation, useQueryClient } from '@tanstack/react-query';

import { FulfillmentActionItemCode } from '@ekhodealer/ekho-common';

import type { MarkInTransitToBuyerResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';

import { updateProgressCache } from './progressCacheUtils';


/**
 * Input type for marking a vehicle as in transit to buyer
 */
export interface MarkInTransitToBuyerInput {
	/** Estimated delivery date (top-level field in ms since epoch) */
	estimatedDeliveryDate?: number;
	/** Fulfillment details (nested object) */
	fulfillmentDetails?: {
		vehicleTrackingReference?: string;
		contactNumber?: string;
		companyName?: string;
	};
}

/**
 * API function to mark a vehicle as in transit to buyer
 */
export const markInTransitToBuyer = async (
	servicesProviderId: string,
	orderId: string,
	input?: MarkInTransitToBuyerInput,
): Promise<MarkInTransitToBuyerResponse> => {
	const response = await apiClient.post<MarkInTransitToBuyerResponse>(
		ordersEndpoints.markInTransitToBuyer(servicesProviderId, orderId),
		input || {},
	);
	return response.data;
};

/**
 * Hook for marking a purchased vehicle (order) as in transit to buyer
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useMarkInTransitToBuyer = (orderId: string | undefined) => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (input?: MarkInTransitToBuyerInput) => {
			if (!orderId || !servicesProviderId) {
				throw new Error(
					'Unable to mark as in transit without an order ID or services provider ID.',
				);
			}

			return markInTransitToBuyer(servicesProviderId, orderId, input);
		},
		onSuccess: (data) => {
			if (!orderId || !servicesProviderId) return;
			updateProgressCache(queryClient, servicesProviderId, orderId, data.progress, FulfillmentActionItemCode.MARK_IN_TRANSIT_TO_BUYER);
		},
	});
};
